import { create } from "zustand";
import type { Station } from "@/apis/station/station-apis";

/** Block configuration for customer restrictions (e.g., age verification) */
export interface BlockConfig {
  id: number;
  blockType: string;
  enabled: boolean;
  config?: {
    minimumAge?: number;
    blockMessage?: string;
  };
}

export interface StationsState {
  blockConfigs: BlockConfig[] | null;
  blockConfigsSuccess: boolean | null;
  saveBlockConfigSuccess: boolean | null;
  deleteBlockConfigSuccess: boolean | null;

  stationsByCity: Station[] | null;
  linkedStations: Station[] | null;
}

interface StationsActions {
  setBlockConfigs: (configs: BlockConfig[] | null) => void;
  setBlockConfigsSuccess: (success: boolean | null) => void;
  setSaveBlockConfigSuccess: (success: boolean | null) => void;
  setDeleteBlockConfigSuccess: (success: boolean | null) => void;

  setStationsByCity: (stations: Station[] | null) => void;
  setLinkedStations: (stations: Station[] | null) => void;

  reset: () => void;
}

const initialState: StationsState = {
  blockConfigs: null,
  blockConfigsSuccess: null,
  saveBlockConfigSuccess: null,
  deleteBlockConfigSuccess: null,
  stationsByCity: null,
  linkedStations: null,
};

export const useStationsStore = create<StationsState & StationsActions>(
  (set) => ({
    ...initialState,

    setBlockConfigs: (configs) => set({ blockConfigs: configs }),
    setBlockConfigsSuccess: (success) => set({ blockConfigsSuccess: success }),
    setSaveBlockConfigSuccess: (success) =>
      set({ saveBlockConfigSuccess: success }),
    setDeleteBlockConfigSuccess: (success) =>
      set({ deleteBlockConfigSuccess: success }),

    setStationsByCity: (stations) => set({ stationsByCity: stations }),
    setLinkedStations: (stations) => set({ linkedStations: stations }),

    reset: () => set(initialState),
  })
);

export const useBlockConfigs = () =>
  useStationsStore((state) => state.blockConfigs);
export const useBlockConfigsSuccess = () =>
  useStationsStore((state) => state.blockConfigsSuccess);
export const useSaveBlockConfigSuccess = () =>
  useStationsStore((state) => state.saveBlockConfigSuccess);
export const useDeleteBlockConfigSuccess = () =>
  useStationsStore((state) => state.deleteBlockConfigSuccess);
export const useStationsByCity = () =>
  useStationsStore((state) => state.stationsByCity);
export const useLinkedStations = () =>
  useStationsStore((state) => state.linkedStations);
